import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, createElementVNode as _createElementVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { reactive, ref, computed, PropType, onMounted } from 'vue'
import { useCmTableListRefresh } from '@/hooks/use-cm-table-list-refresh'
import CmTable from '@/components/cmTable/index.vue'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import PolicySelect from '@/views/dashboard/_components/policy-select/policy-select.vue'
import GroupTreeSelector from '@/views/dashboard/_components/group-tree-selector/group-tree-selector.vue'
import { TerminalType } from '@/views/dashboard/_enum/terminal-type'
import { listCol } from '@/utils/list-columns-i18n'
import { TableOpt, TableActOpt } from '@/components/cmTable/types'
import { TableViewMode, TableViewModeType } from '@/views/dashboard/_enum/table-view-mode'
import { formatTimeStamp } from '@/utils/time'
import { SoftDisabledLogService, LogDataReq, LogData } from '@/views/dashboard/notice-manage/soft-disabled-log/soft-disabled-log.service'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { useParseTime } from '@/hooks'
import { FormType, ObjectFormItem, DateType, FormSize } from '@/components/cmForm/types'
import RawDataDrawer from '@/views/dashboard/_components/raw-data-drawer/raw-data-drawer.vue'
import ExportDropdown from '@/views/dashboard/_components/export-dropdown/export-dropdown.vue'
import { ExportType } from '@/views/dashboard/_components/export-dropdown/export-dropdown.service'
import { PolicyType } from '@/views/dashboard/_enum/policy-type'
import dayjs from 'dayjs'
import { useI18n } from 'vue-i18n'
import { formatTableEmpty } from '@/utils/format'
import { computerInfoCols } from '@/views/dashboard/data-safe/ransomware-protection/types/ransomware-incident'

type ExcludeRequestParams = Extract<keyof LogDataReq, 'key_word' | 'limit' | 'page' | 'start_time' | 'end_time'>

export default /*#__PURE__*/_defineComponent({
  __name: 'software-disabled-log-list',
  props: {
  viewMode: {
    type: String as PropType<TableViewModeType>,
    default: TableViewMode.preview
  },
  hasPermission: {
    type: Boolean,
    default: true
  },
  /** 打开抽屉时预选的策略 id（如从策略列表「执行时间」进入） */
  presetStrategyId: {
    type: String,
    default: undefined,
  },
  /** 打开抽屉时预设的日期范围 YYYY-MM-DD（如近一月） */
  presetDateRange: {
    type: Array as unknown as PropType<[string, string] | undefined>,
    default: undefined,
  },
  /** 打开抽屉时预填的搜索关键词（如从排行/分布图点击进入） */
  presetKeyWord: {
    type: String,
    default: undefined,
  },
  /** 打开抽屉时预选的终端分组 id（与分布图 group_id 一致） */
  presetGroupId: {
    type: Number,
    default: undefined,
  },
},
  emits: ['viewAll'],
  setup(__props, { emit: __emit }) {

const { t, locale } = useI18n()

function triggerResultDescText(value: number): string {
  const map: Record<number, string> = {
    0: t('softwareDisabledLog.triggerResultDesc0'),
    1: t('softwareDisabledLog.triggerResultDesc1'),
    2: t('softwareDisabledLog.triggerResultDesc2'),
  }
  return map[value] ?? '--'
}

const props = __props

const emits = __emit

const injector = useReflectiveInjector([SoftDisabledLogService])
const service = injector.get(SoftDisabledLogService)
const dataApi = service.getLogData.bind(service)

const tableRef = ref()

const { refreshList } = useCmTableListRefresh(tableRef)

const isShowRawData = ref(false)
const currentRow = ref<Record<string, unknown>>({})
// full：有 preset 用 preset；否则默认近三个月。preview：不设默认日期
const initialDateRange =
  props.viewMode === TableViewMode.full
    ? (props.presetDateRange?.length === 2
      ? props.presetDateRange
      : ([dayjs().subtract(3, 'month').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')] as [string, string]))
    : undefined
const [initialStartTime, initialEndTime] = useParseTime(initialDateRange || [])

function initialGroupIdStr () {
  if (props.presetGroupId != null && Number.isFinite(props.presetGroupId)) return String(props.presetGroupId)
  return '-1'
}

const queryParams = reactive<any>({
  key_word: props.presetKeyWord?.trim() || '',
  group_id: initialGroupIdStr(),
  start_time: initialStartTime != null ? String(initialStartTime) : undefined,
  end_time: initialEndTime != null ? String(initialEndTime) : undefined,
  handle_type: undefined,
  interval_type: undefined,
  page: 1,
  limit: props.viewMode === TableViewMode.preview ? 10 : 20,
  strategy_id: props.presetStrategyId,
  dateRange: initialDateRange
})

const groupTreeDefaultId = computed<number | null>(() => {
  const g = queryParams.group_id
  if (g == null || g === '' || g === '-1') return null
  const n = Number(g)
  return Number.isFinite(n) ? n : null
})

const filterFormColumns = computed<Partial<ObjectFormItem<any, any>>>(() => {
  void locale.value
  return {
    strategy_id: {
      colOpt: { style: 'flex: 0 0 130px' },
      slot: true,
      label: t('softwareDisabledLog.triggerPolicy'),
      hiddenAllLabel: true,
      outSearchFiledFlag: true,
      events: {
        change: () => void refreshList(),
      },
    },
    group_id: {
      colOpt: { style:'flex:0 0 150px' },
      slot: true,
      label: listCol(t, 'groupName'),
      hiddenAllLabel: true,
      outSearchFiledFlag: true,
      type: FormType.Select,
      events: {
        change: () => void refreshList(),
      },
    },
    handle_type: {
      colOpt: { span: 12 },
      label: t('softwareDisabledLog.triggerResult'),
      type: FormType.Select,
      selectOpt: { clearable: true },
      dictData: [
        { label: t('softwareDisabledLog.triggerResultOption0'), value: 0 },
        { label: t('softwareDisabledLog.triggerResultOption1'), value: 1 },
        { label: t('softwareDisabledLog.triggerResultOption2'), value: 2 },
      ]
    },
    interval_type: {
      colOpt: { span: 12 },
      label: t('softwareDisabledLog.bwList'),
      type: FormType.Select,
      selectOpt: { clearable: true },
      dictData: [
        { label: t('softwareDisabledLog.blacklist'), value: 1 },
        { label: t('softwareDisabledLog.whitelist'), value: 2 }
      ]
    },
    dateRange: {
      colOpt: { style: 'flex: 0 0 280px' },
      label: t('softwareDisabledLog.dateRange'),
      type: FormType.Date,
      dateType: DateType.DateRange,
      events: {
        change: (val: any) => {
          const [start, end] = useParseTime(val)
          queryParams.start_time = start != null ? String(start) : undefined
          queryParams.end_time = end != null ? String(end) : undefined
          handleQuery()
        },
      },
      dateOpt: {
        'value-format': 'YYYY-MM-DD',
        'start-placeholder': t('common.startDate'),
        'end-placeholder': t('common.endDate'),
        clearable: false,
      },
    },
  }
})

const tableActOpt = computed<TableActOpt>(() => {
  void locale.value
  return {
    columnOpt: { label: t('listColumns.operation'), width: 120, align: 'left', fixed: 'right' },
    actionButtons: () => [
      {
        name: t('noticeManage.rawData'),
        type: 'rawData',
        opt: { size: FormSize.Small, text: true, type: 'text' },
      },
    ],
  }
})

const tableOpt = computed<TableOpt>(() => {
  void locale.value
  return {
    bodyStyle: props.viewMode === TableViewMode.full ? 'padding: 10px 16px' : 'background: transparent;padding:0',
    containerOpt: { style: 'padding: 0;background: var(--cm-color-card-bg)' },
    path: 'list',
    totalPath: 'total',
    height: props.viewMode === TableViewMode.preview ? undefined : '100%',
    showActionBar: false,
    showForm: props.viewMode === TableViewMode.full,
    showRightToolbar: false,
    showSearch: false,
    rowKey: 'id',
    selection: false,
    showTip: false,
    usePagination: false,
    disableScrollLoad: props.viewMode === TableViewMode.preview,
    scrollLoadThreshold: 10,
    scrollLoadDelay: 100,
    columns: [
      {
        label: t('softwareDisabledLog.triggerTime'),
        prop: 'report_time',
        fixed: 'left',
        minWidth:160,
        formatter: (_row: any, value: number) => formatTimeStamp(value, 'YYYY-MM-DD HH:mm'),
      },
      {
        label: t('softwareDisabledLog.triggerPolicy'),
        prop: 'strategy_name',minWidth:160,
      },
      {
        label: t('softwareDisabledLog.policyAttr'),
        prop: 'interval_type',minWidth:160,
      },
      {
        label: t('softwareDisabledLog.triggerSoft'),
        prop: 'soft_name',
         columnOpt: { minWidth: 150, 'show-overflow-tooltip': true },
      },
      {
        label: t('noticeManage.processPath'),
        prop: 'path',
        columnOpt: { minWidth: 150, 'show-overflow-tooltip': true },
        formatter: (row: LogData, value: unknown, column: unknown) =>
          formatTableEmpty(row, column, value),minWidth:200,
      },
      {
        label: t('noticeManage.processMd5'),
        prop: 'md5',
        columnOpt: { minWidth: 150, 'show-overflow-tooltip': true },
        formatter: (row: LogData, value: unknown, column: unknown) => value || '--',minWidth:200,
      },
      {
        label: t('noticeManage.signature'),
        prop: 'signature',
        columnOpt: { minWidth: 150, 'show-overflow-tooltip': true },
        formatter: (row: LogData, value: unknown, column: unknown) => value || '--',minWidth:200,
      },
      {
        label: t('softwareDisabledLog.softVersion'),
        prop: 'soft_version',
        columnOpt: { 'show-overflow-tooltip': true },minWidth:140,
        formatter: (row: LogData, value: unknown, column: unknown) => value || '--',
      },
      {
        label: t('softwareDisabledLog.triggerResult'),
        prop: 'handle_type',
        width: 230,
      },
      ...computerInfoCols(true,true,t)
    ],
  }
})

async function handleSearch(value: string) {
  queryParams.key_word = value
  queryParams.page = 1
  await refreshList()
}

async function handleQuery() {
  queryParams.page = 1
  await refreshList()
}

function handleQueryChange() {
  queryParams.page = 1
  void refreshList()
}

async function updateGroupId (groupId: number) {
  queryParams.group_id = String(groupId)
  queryParams.page = 1
  await refreshList()
}

function handleReset() {
  queryParams.key_word = ''
  queryParams.group_id = '-1'
  queryParams.handle_type = undefined
  queryParams.interval_type = undefined
  queryParams.strategy_id = undefined
  // full 模式重置为最近三个月，preview 模式清空日期
  if (props.viewMode === TableViewMode.full) {
    const range = [dayjs().subtract(3, 'month').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')] as [string, string]
    const [start, end] = useParseTime(range)
    queryParams.dateRange = range
    queryParams.start_time = start != null ? String(start) : undefined
    queryParams.end_time = end != null ? String(end) : undefined
  } else {
    queryParams.dateRange = undefined
    queryParams.start_time = undefined
    queryParams.end_time = undefined
  }
  queryParams.page = 1
  void refreshList()
}

function handleAction ({ type, rowItem }: { type: string; rowItem?: LogData & Record<string, unknown> }) {
  if (type === 'rawData' && rowItem) {
    currentRow.value = rowItem as Record<string, unknown>
    isShowRawData.value = true
  }
}

function download (type: ExportType) {
  const gid = queryParams.group_id
  const group_id =
    gid != null && gid !== '' && String(gid) !== '-1' ? String(gid) : undefined
  const handle_type =
    queryParams.handle_type !== undefined && queryParams.handle_type !== null
      ? queryParams.handle_type
      : -1
  const requestParams: LogDataReq = {
    key_word: queryParams.key_word || undefined,
    start_time: queryParams.start_time as LogDataReq['start_time'],
    end_time: queryParams.end_time as LogDataReq['end_time'],
    handle_type,
    interval_type: queryParams.interval_type,
    page: 0,
    limit: 0,
    strategy_id: queryParams.strategy_id || undefined,
    group_id,
  }
  void service.download(type, requestParams, t('noticeManage.exportFileSoftDisabledLog'))
}

// 重写 dataApi 以处理查询参数
const wrappedDataApi = async (params: any) => {
  // CmTable 会将 queryParams 和 form 合并后传递，需要提取正确的参数
  // 合并参数，优先使用 params（CmTable 传递的），其次使用 queryParams
  const mergedParams = {
    ...queryParams,
    ...params
  }

  const gid = mergedParams.group_id
  const group_id =
    gid != null && gid !== '' && String(gid) !== '-1' ? String(gid) : undefined

  const requestParams: LogDataReq = {
    key_word: mergedParams.key_word || '',
    start_time: mergedParams.start_time,
    end_time: mergedParams.end_time,
    // 处理 handle_type：如果值为 0 或明确设置，则使用；否则为 undefined
    handle_type: (mergedParams.handle_type !== undefined && mergedParams.handle_type !== null && mergedParams.handle_type !== -1)
      ? mergedParams.handle_type
      : undefined,
    interval_type: mergedParams.interval_type || undefined,
    page: mergedParams.page || 1,
    limit: mergedParams.limit || (props.viewMode === TableViewMode.preview ? 10 : 20),
    strategy_id: mergedParams.strategy_id || undefined,
    group_id,
  }
  return service.getLogData(requestParams)
}

onMounted(async () => {
  if (!props.hasPermission) return
  await refreshList()
})

return (_ctx: any,_cache: any) => {
  const _component_el_tag = _resolveComponent("el-tag")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(CmTable, {
      ref_key: "tableRef",
      ref: tableRef,
      tableOpt: tableOpt.value,
      tableActOpt: tableActOpt.value,
      firstLoading: false,
      "has-permission": __props.hasPermission,
      api: wrappedDataApi,
      modelValue: queryParams,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((queryParams) = $event)),
      pageOption: {
      style: 'padding-bottom: 16px',
      pageNumName: 'page',
      pageSizeName: 'limit',
    },
      onHandleAction: handleAction
    }, {
      header: _withCtx(() => [
        (props.viewMode === _unref(TableViewMode).full)
          ? (_openBlock(), _createBlock(SearchFilterBar, {
              key: 0,
              style: {"padding":"0","background-color":"transparent"},
              modelValue: queryParams,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((queryParams) = $event)),
              "form-columns": filterFormColumns.value,
              disabled: !props.hasPermission,
              "show-search": true,
              "sync-search-from-field": "key_word",
              "search-placeholder": _ctx.$t('common.phComputerUserSoft'),
              "popover-threshold": 2,
              onSearch: handleSearch,
              onQuery: handleQuery,
              onReset: handleReset
            }, {
              strategy_id: _withCtx(() => [
                _createVNode(PolicySelect, {
                  width: "100%",
                  size: "small",
                  disabled: !props.hasPermission,
                  modelValue: queryParams.strategy_id,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((queryParams.strategy_id) = $event)),
                  "strategy-type": _unref(PolicyType).softwareDisabled,
                  onChange: handleQueryChange
                }, null, 8, ["disabled", "modelValue", "strategy-type"])
              ]),
              group_id: _withCtx(() => [
                _createVNode(GroupTreeSelector, {
                  disabled: !props.hasPermission,
                  "terminal-type": [_unref(TerminalType).windows, _unref(TerminalType).windowsServer],
                  "default-group-id": groupTreeDefaultId.value,
                  onSelected: updateGroupId
                }, null, 8, ["disabled", "terminal-type", "default-group-id"])
              ]),
              actions: _withCtx(() => [
                (props.viewMode === _unref(TableViewMode).full)
                  ? (_openBlock(), _createBlock(ExportDropdown, {
                      key: 0,
                      "button-text-i18n-key": "noticeManage.exportRecord",
                      "has-permission": props.hasPermission,
                      onExport: download
                    }, null, 8, ["has-permission"]))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }, 8, ["modelValue", "form-columns", "disabled", "search-placeholder"]))
          : _createCommentVNode("", true)
      ]),
      interval_type: _withCtx(({ value }: any) => [
        _createVNode(_component_el_tag, {
          type: value === 1 ? 'danger' : 'success',
          size: "small"
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(value === 1 ? _ctx.$t('softwareDisabledLog.blacklist') : _ctx.$t('softwareDisabledLog.whitelist')), 1)
          ]),
          _: 2
        }, 1032, ["type"])
      ]),
      handle_type: _withCtx(({ value }: any) => [
        _createElementVNode("span", null, _toDisplayString(triggerResultDescText(value)), 1)
      ]),
      _: 1
    }, 8, ["tableOpt", "tableActOpt", "has-permission", "modelValue"]),
    _createVNode(RawDataDrawer, {
      modelValue: isShowRawData.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((isShowRawData).value = $event)),
      "raw-data": currentRow.value
    }, null, 8, ["modelValue", "raw-data"])
  ], 64))
}
}

})